/* eslint-disable no-param-reassign */
import React, { useCallback, useRef, useState } from "react";
import Dropzone from "react-dropzone";
import Compress from "browser-image-compression";

import FileMessagesModal from "../../../../common/FileMessagesModal/FileMessagesModal";

import { ReactComponent as EyeShow } from "../../../../../assets/images/habitacional/eye-show.svg";
import { ReactComponent as RefreshIcon } from "../../../../../assets/images/habitacional/refresh.svg";
import { ReactComponent as MenuDots } from "../../../../../assets/images/habitacional/menu-dots.svg";
import { ReactComponent as Camera } from "../../../../../assets/images/habitacional/camera.svg";
import { ReactComponent as Trash } from "../../../../../assets/images/habitacional/trash.svg";
import { ReactComponent as ErrorIcon } from "../../../../../assets/images/habitacional/error.svg";
import { ReactComponent as IconError } from "../../../../../assets/images/habitacional/rounded-warning.svg";
import { generateBase64 } from "../../../../../utils/generateBase64";

import * as S from "./InputFile.styled";

const getFileName = (error) => (error ? "Tentar de novo" : "Mudar arquivo");

const InputFile = ({
  title,
  subTitle,
  details,
  file,
  setFile,
  maxSize,
  errorMessage,
  ...props
}) => {
  const hiddenFileInput = useRef(null);
  const hiddenCameraInput = useRef(null);
  const dropRef = useRef(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(1);
  const [shouldShowButtons, setShouldShowButtons] = useState(true);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const onProgress = (percentage) => {
    setProgress(percentage);
  };
  const compressImage = useCallback(
    (imgFile) => {
      const options = { maxSizeMB: 0.6, useWebWorker: true, onProgress };
      Compress(imgFile, options)
        .then((compressedBlob) => {
          compressedBlob.lastModifiedDate = new Date();
          const convertedBlobFile = new File([compressedBlob], imgFile.name, {
            type: imgFile.type,
            lastModified: Date.now(),
          });
          generateBase64(convertedBlobFile, setFile, setError);
          setLoading(false);
          setProgress(0);
        })
        .catch(() => {
          setError("Ops! Algo deu errado com o seu arquivo.");
          setLoading(false);
          setProgress(0);
        });
    },
    [setFile]
  );

  const onFileChange = useCallback(
    (e) => {
      setShouldShowButtons(true);
      const uploadedFile = e?.target?.files[0] || e[0];
      if (!uploadedFile) {
        return;
      }

      const acceptedTypes = [
        "application/pdf",
        "image/jpg",
        "image/png",
        "image/jpeg",
      ];
      const fileType = uploadedFile?.type;
      const fileSizeKB = uploadedFile?.size / 1000;

      setLoading(true);

      if (!acceptedTypes.includes(fileType)) {
        setError(
          "Tipo de arquivo não suportado. Apenas imagens ou PDF são aceitos."
        );
        setLoading(false);
        return;
      }

      if (fileSizeKB > maxSize * 1056) {
        setIsOpenModal(true);
        setError(`Tamanho máximo do arquivo é ${maxSize} MB`);
        setLoading(false);
        return;
      }

      if (fileType.includes("image")) {
        compressImage(uploadedFile);
      } else {
        generateBase64(uploadedFile, setFile, setError);
        setError("");
        setLoading(false);
      }
    },
    [compressImage, maxSize, setFile]
  );

  const handleTakePicture = () => {
    hiddenCameraInput.current.click();
  };

  const showFile = () => {
    if (file?.file) {
      const objectURL = URL.createObjectURL(file.file);
      window.open(objectURL, "_blank");
    }
  };
  return (
    <>
      <div {...props}>
        <Dropzone
          onDrop={onFileChange}
          onDragOver={() => setShouldShowButtons(false)}
          onDragLeave={() => setShouldShowButtons(true)}
          accept="image/jpg, image/png, application/pdf"
        >
          {({ getRootProps, getInputProps }) => {
            const fileInputTitle = error || title;
            return (
              <S.FileInputContainer
                {...getRootProps()}
                ref={dropRef}
                dragging={!shouldShowButtons}
                success={file}
                error={error}
              >
                <S.ContainerColumn>
                  <S.TitleContainer>
                    {file?.name ? (
                      <S.FileInputTitleLink
                        onClick={showFile}
                        $shouldBeInFront={shouldShowButtons}
                      >
                        <EyeShow /> {file.name}
                      </S.FileInputTitleLink>
                    ) : (
                      <S.FileInputTitle variant="headline-04" error={error}>
                        {fileInputTitle}
                      </S.FileInputTitle>
                    )}
                    {file && !loading && <S.StyledSuccessCircle />}
                    {!file && error && <ErrorIcon />}
                  </S.TitleContainer>
                  {file || error ? (
                    <>
                      <S.FileInputSubTitleLight success={file} error={error}>
                        {title}
                      </S.FileInputSubTitleLight>
                      <S.FileInputSubTitleLight success={file} error={error}>
                        {subTitle}
                      </S.FileInputSubTitleLight>
                    </>
                  ) : (
                    <S.FileInputSubTitle>{subTitle}</S.FileInputSubTitle>
                  )}
                </S.ContainerColumn>
                {!file && !loading && !error && (
                  <S.ContainerRow
                    $shouldBeInFront={shouldShowButtons}
                    onClick={handleTakePicture}
                  >
                    <S.StyledUploadDoc $shouldBeInFront={shouldShowButtons} />
                    <S.StyledLink>Clique ou solte o arquivo aqui</S.StyledLink>
                  </S.ContainerRow>
                )}
                {loading && progress && (
                  <S.StyledProgressBar value={progress} />
                )}
                {(file && !loading) || error ? (
                  <S.ModifyButtonsContainer
                    $shouldBeInFront={shouldShowButtons}
                    success={file}
                  >
                    <S.FlexRow
                      style={{ cursor: "pointer" }}
                      success={file}
                      error={error}
                    >
                      <RefreshIcon />
                      <S.ButtonText>{getFileName(error)}</S.ButtonText>
                    </S.FlexRow>
                    {file && (
                      <>
                        <S.Bar />
                        <S.FlexRow
                          onClick={() => {
                            setFile(null);
                            setError("");
                          }}
                          style={{ cursor: "pointer" }}
                          success={file}
                          error={error}
                        >
                          <Trash /> <S.ButtonText>Excluir arquivo</S.ButtonText>
                        </S.FlexRow>
                      </>
                    )}
                  </S.ModifyButtonsContainer>
                ) : (
                  !loading && (
                    <>
                      <S.StyledMobileButtons
                        $shouldBeInFront={shouldShowButtons}
                      >
                        <S.FlexRow
                          style={{ cursor: "pointer" }}
                          onClick={handleTakePicture}
                          success={file}
                          error={error}
                        >
                          <MenuDots />{" "}
                          <S.ButtonText>Escolher do celular</S.ButtonText>
                        </S.FlexRow>
                        <S.Bar />
                        <S.FlexRow
                          onClick={handleTakePicture}
                          style={{ cursor: "pointer" }}
                          success={file}
                          error={error}
                        >
                          <Camera /> <S.ButtonText>Tirar foto</S.ButtonText>
                        </S.FlexRow>
                      </S.StyledMobileButtons>
                      <S.FileInputDetails>{details}</S.FileInputDetails>
                      <S.FileInputDetails>
                        {" "}
                        Tamanho máximo {maxSize} MB{" "}
                      </S.FileInputDetails>
                    </>
                  )
                )}

                <input
                  {...getInputProps()}
                  type="file"
                  ref={hiddenFileInput}
                  style={{ display: "none" }}
                  onChange={onFileChange}
                  accept="image/jpg, image/png, application/pdf"
                />
                <input
                  ref={hiddenCameraInput}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={onFileChange}
                  style={{ display: "none" }}
                />
                {!file && errorMessage && (
                  <S.ErrorMessage>
                    <IconError />
                    {errorMessage}
                  </S.ErrorMessage>
                )}
              </S.FileInputContainer>
            );
          }}
        </Dropzone>
      </div>

      {/* Modal */}
      <FileMessagesModal
        show={isOpenModal}
        onClose={() => setIsOpenModal((state) => !state)}
      />
    </>
  );
};

export default InputFile;
